// @flow

import * as React from 'react';

import { ProductKindBadge } from '@gandi/cart';
import type { ProductId, Process, ProductType, ProductKind } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Base, Text } from '@gandi/ui';
import { ListRow, ListItem } from '@gandi/ui/deprecated';

import { useFeatureFlags } from 'src/hooks/useFeatureFlag';

import { ListRow as SuperNewListRow } from '../../../ListRow';

import ProductSetup from '../ProductSetup';
import {
  useDomainPerksDetails,
  useWebhostingPerksDetails,
} from '../modules/hooks';

import Autorenew from './Autorenew';

import ResponsiveDivider from './ResponsiveDivider';
import ResponsiveTitleCell from './ResponsiveTitleCell';

function useLabel(kind: ProductKind): string {
  const { __ } = useTranslator();

  switch (kind) {
    case 'DomainCreate':
      return __('Domain Registration');

    case 'services':
    case 'CorporateCreate':
      return __('Services');

    case 'DomainRenew':
      return __('Domain Renewal');

    case 'DomainRestore':
      return __('Domain Restore');

    case 'DomainTransfer':
      return __('Domain Transfer');

    case 'DomainChangeOwner':
      return __('Domain Owner Change');

    case 'CertificateCreate':
      return __('SSL Certificate Creation');

    case 'CloudAdd':
      return __('Cloud Resources');

    case 'PaasCreate':
      return __('Hosting Creation');

    case 'PaasRenew':
      return __('Hosting Renewal');

    case 'PaasUpdate':
      return __('Hosting Update');

    case 'MailboxCreate':
    case 'MailboxStandAloneCreate':
    case 'Mailboxv2Create':
      return __('Mailbox Creation');

    case 'MailboxUpdate':
    case 'Mailboxv2Update':
      return __('Mailbox Update');

    case 'MailboxRenew':
    case 'Mailboxv2Renew':
      return __('Mailbox Renew');

    case 'MailboxConvert':
    case 'Mailboxv2Convert':
      return __('Mailbox Conversion');

    case 'MarketplaceCreate':
      return __('Marketplace Service Creation');

    case 'MarketplaceRenew':
      return __('Marketplace Service Renewal');

    case 'MarketplaceUpdate':
      return __('Marketplace Service Update');

    case 'MarketplaceDelete':
      return __('Marketplace Service Deletion');

    case 'PremiumDNSCreate':
      return __('Domain Options');

    default:
      return __('Other products');
  }
}

const AUTORENEW_ENABLED = [
  'DomainCreate',
  'DomainTransfer',
  'PaasCreate',
  'promotional',
];

type Props = {
  authenticated: boolean,
  cartProductOwner: ?string,
  firstProductId: ProductId,
  hasWebhosting: boolean,
  kind: ProductKind,
  productType: ProductType,
  process: Process,
};

function ProductKindHeader({
  authenticated,
  cartProductOwner,
  firstProductId,
  hasWebhosting,
  kind,
  process,
  productType,
}: Props) {
  const { FF_CART_OVERVIEW_NDS } = useFeatureFlags();

  const title = useLabel(kind);

  const showSetup = authenticated && kind !== 'services';

  if (FF_CART_OVERVIEW_NDS) {
    AUTORENEW_ENABLED.push('MailboxStandAloneCreate');
  }

  const showAutorenew =
    authenticated &&
    !cartProductOwner &&
    !hasWebhosting &&
    AUTORENEW_ENABLED.includes(kind);

  if (FF_CART_OVERVIEW_NDS) {
    return (
      <>
        <ProductKindBadge kind={kind} productType={productType} />

        {showSetup && (
          <ProductSetup
            process={process}
            productType={productType}
            firstProductId={firstProductId}
          />
        )}

        {showAutorenew && (
          <Autorenew kind={kind} productType={productType} process={process} />
        )}

        {kind === 'DomainCreate' && <IncludedWithDomain />}

        {kind === 'PaasCreate' && <IncludedWithPaas />}
      </>
    );
  }

  /** Legacy style */
  return (
    <>
      <ListRow>
        <ListItem backgroundColor="white">
          <ResponsiveDivider>
            <ResponsiveTitleCell>{title}</ResponsiveTitleCell>
            {showAutorenew && (
              <Base flex flexAlignItemsCenter>
                <Autorenew
                  key={`autorenew-${kind}`}
                  kind={kind}
                  productType={productType}
                  process={process}
                />
              </Base>
            )}
          </ResponsiveDivider>
        </ListItem>
      </ListRow>

      {showSetup && (
        <ProductSetup
          process={process}
          productType={productType}
          firstProductId={firstProductId}
        />
      )}
    </>
  );
}

export default (React.memo<Props>(
  ProductKindHeader,
): React.ComponentType<Props>);

function IncludedWithDomain(): React.MixedElement {
  const { __ } = useTranslator();
  const perks = useDomainPerksDetails();
  return (
    <Base marginTop="xsmall">
      <SuperNewListRow
        intent="muted"
        title={__('Included with every domain purchase')}
        options={perks.map(({ label, detail }) => (
          <Perk key={label} label={label} detail={detail} />
        ))}
        price={<Text color="weak">{__('Free')}</Text>}
      />
    </Base>
  );
}

function IncludedWithPaas(): React.MixedElement {
  const { __ } = useTranslator();
  const perks = useWebhostingPerksDetails();
  return (
    <Base marginTop="xsmall">
      <SuperNewListRow
        intent="muted"
        title={__('Included with every Web Hosting purchase')}
        options={perks.map(({ label, detail }) => (
          <Perk key={label} label={label} detail={detail} />
        ))}
        price={<Text color="weak">{__('Free')}</Text>}
      />
    </Base>
  );
}

function Perk({
  label,
  detail,
}: {
  label: string,
  detail?: string,
}): React.MixedElement {
  return (
    <>
      {label} {detail && <Text color="weak">– {detail}</Text>}
    </>
  );
}
